import React from 'react';
import { Drawer } from '@mui/material';
import { LeftMenu, LeftMenu_BottomSection } from '../../constants/LeftMenu';
import { ACM } from '../../constants/UserRoles';
import { useLeftPanel } from './useLeftPanel';
import { renderMenuItems } from './menuUtils';
import LeftPanelHeader from './LeftPanelHeader';
import MenuSection from './MenuSection';
import DrawerToggleButton from './DrawerToggleButton';

import './LeftPanel.css';

/**
 * LeftPanel - Main navigation drawer component for the Mindly application
 * 
 * This component renders a collapsible sidebar navigation that adapts to different user roles.
 * It includes role-based menu items, patient lists for doctors/teachers, and role switching
 * functionality for users with multiple roles.
 * 
 * @param {Object} props - Component props
 * @param {string} props.userType - The user's role type (ADMIN, DOCTOR, TEACHER, etc.)
 * @returns {JSX.Element} Rendered LeftPanel component
 */
const LeftPanel = React.memo(({ userType }) => {
  const {
    user,
    typeOfUser,
    selectedMenuKey,
    isNewMesage,
    isDrawerExpanded,
    isHovering,
    menuItemsToRender,
    drawerWidth,
    handleMenuClick,
    handleRoleToggle,
    handleMouseEnter,
    handleMouseLeave,
    setIsDrawerExpanded,
  } = useLeftPanel(userType);

  const renderMenuItemsWithProps = (items) =>
    renderMenuItems(
      items,
      userType,
      selectedMenuKey,
      handleMenuClick,
      isDrawerExpanded,
      isNewMesage,
      isHovering
    );

  const shouldShowRoleToggle = 
    (userType === 'TEACHER' || userType === 'SCHOOLADMIN') &&
    Array.isArray(user?.roles) &&
    user.roles.length > 1;

  return (
    <>
      <Drawer
        className='left-drawer'
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          position: 'absolute',
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            zIndex: 1200,
            transition: 'width 0.2s ease',
            height: '100vh',
            overflowY: 'auto',
            overflowX: 'hidden',
          },
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        variant='permanent'
        anchor='left'
      >
        <LeftPanelHeader 
          isDrawerExpanded={isDrawerExpanded} 
          isHovering={isHovering} 
        />

        {ACM[userType]?.hasAdminMenu && (
          <MenuSection
            userType={userType}
            menuItems={menuItemsToRender}
            renderMenuItems={renderMenuItemsWithProps}
          />
        )}

        {ACM[userType]?.hasSchoolAdminMenu && (
          <MenuSection
            userType={userType}
            menuItems={menuItemsToRender}
            bottomMenuItems={LeftMenu_BottomSection}
            renderMenuItems={renderMenuItemsWithProps}
            typeOfUser={typeOfUser}
            user={user}
            handleRoleToggle={handleRoleToggle}
            showRoleToggle={shouldShowRoleToggle}
          />
        )}

        {ACM[userType]?.hasOrganizationAdminMenu && (
          <MenuSection
            userType={userType}
            menuItems={menuItemsToRender}
            bottomMenuItems={LeftMenu_BottomSection}
            renderMenuItems={renderMenuItemsWithProps}
          />
        )}

        {(ACM[userType]?.hasStudentMenu || ACM[userType]?.hasPatientMenu) && (
          <MenuSection
            userType={userType}
            menuItems={menuItemsToRender}
            bottomMenuItems={LeftMenu_BottomSection}
            renderMenuItems={renderMenuItemsWithProps}
          />
        )}

        {ACM[userType]?.hasUserMenu && (
          <MenuSection
            userType={userType}
            menuItems={LeftMenu}
            bottomMenuItems={LeftMenu_BottomSection}
            renderMenuItems={renderMenuItemsWithProps}
            typeOfUser={typeOfUser}
            user={user}
            handleRoleToggle={handleRoleToggle}
            showRoleToggle={shouldShowRoleToggle}
          />
        )}
      </Drawer>

      <DrawerToggleButton
        isDrawerExpanded={isDrawerExpanded}
        isHovering={isHovering}
        onToggle={() => setIsDrawerExpanded((prev) => !prev)}
      />
    </>
  );
});

export default LeftPanel;
